import React, { useState, useRef, useEffect, useMemo } from 'react';
import ToggleableDetails from '../ToggleableDetails';
import { useTheme } from '../../context/ThemeContext';
import { renderParagraphs } from '../../utils/textFormatters';

const LegendaryWeapons = ({ weapons, contributionOrder, showTrivia }) => {
  const [hoveredWeapon, setHoveredWeapon] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [weaponsOrderBy, setWeaponsOrderBy] = useState('contribution');
  const [isHoveringContainer, setIsHoveringContainer] = useState(false);
  const [isHoveringKey, setIsHoveringKey] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const scrollbarRef = useRef(null);
  const componentRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const scrollDirectionRef = useRef(1);
  const animationRef = useRef(null);
  const isHoveringRef = useRef(false);
  const isDraggingRef = useRef(false);
  const [scrollbarPosition, setScrollbarPosition] = useState(0);
  const orderChangedRef = useRef(false);
  const { darkMode } = useTheme();

  const weaponsArray = weapons?.weapons || [];

  // Use Intersection Observer to detect when component is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null, // viewport
        threshold: 0.1, // trigger when at least 10% of the component is visible
      }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, []);

  // SectionHeader component
  const SectionHeader = ({ title }) => {
    return (
      <div className="mb-8 relative">
        <h2 className="text-3xl font-bold text-[#2978C8] dark:text-[#4f9cf9] inline-block relative z-10">
          {title}
          <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-[#2978C8] to-[#294C6F] dark:from-[#4f9cf9] dark:to-[#3a6ca0]"></span>
        </h2>
      </div>
    );
  };

  const orderedWeapons = useMemo(() => {
    if (weaponsOrderBy === 'release' || !contributionOrder) {
      return weaponsArray;
    } else {
      return contributionOrder
        .map(name => weaponsArray.find(weapon => weapon.name === name))
        .filter(Boolean);
    }
  }, [weaponsArray, contributionOrder, weaponsOrderBy]);

  // Update scrollbar position when container scrolls
  const updateScrollbarPosition = () => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const maxScroll = container.scrollWidth - container.clientWidth;
    if (maxScroll <= 0) return; // Prevent division by zero
    
    const newPosition = container.scrollLeft / maxScroll;
    setScrollbarPosition(newPosition);
  };

  // Animation function that can be called from multiple places
  const startAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    const animate = () => {
      if (!containerRef.current || isHoveringRef.current || isDraggingRef.current || !isVisible) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const container = containerRef.current;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const newPosition = scrollPositionRef.current + (scrollDirectionRef.current * 0.5);

      if (newPosition >= maxScroll) {
        scrollDirectionRef.current = -1;
        scrollPositionRef.current = maxScroll;
      } else if (newPosition <= 0) {
        scrollDirectionRef.current = 1;
        scrollPositionRef.current = 0;
      } else {
        scrollPositionRef.current = newPosition;
      }

      container.scrollLeft = scrollPositionRef.current;
      updateScrollbarPosition();
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  // Reset animation when weapons order changes
  useEffect(() => {
    // Only reset scroll position when weapons order changes
    scrollPositionRef.current = 0;
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
    scrollDirectionRef.current = 1;
    setScrollbarPosition(0);
    
    // Mark that order has changed
    orderChangedRef.current = true;

    // Start animation immediately if component is visible
    if (isVisible) {
      startAnimation();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [weaponsOrderBy]);

  // Handle visibility changes
  useEffect(() => {
    if (isVisible) {
      // Sync the container scroll position with the stored position
      if (containerRef.current) {
        containerRef.current.scrollLeft = scrollPositionRef.current;
        updateScrollbarPosition();
      }
      
      // Start animation if it's not already running
      if (!animationRef.current) {
        startAnimation();
      }
      
      // Reset the order changed flag
      orderChangedRef.current = false;
    }

    return () => {
      // Only cancel animation when component becomes invisible
      // but don't reset the scroll position
      if (!isVisible && animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isVisible]);

  const handleMouseEnter = (weapon, e) => {
    setHoveredWeapon(weapon);
    setMousePos({ x: e.clientX, y: e.clientY });
    isHoveringRef.current = true;
  };

  const handleMouseLeave = () => {
    setHoveredWeapon(null);
    isHoveringRef.current = false;
  };

  const handleKeyMouseEnter = (e) => {
    setIsHoveringKey(true);
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleKeyMouseLeave = () => {
    setIsHoveringKey(false);
  };

  const handleKeyMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  // Handle scrollbar click (jump to position)
  const handleScrollbarClick = (e) => {
    if (!containerRef.current || !scrollbarRef.current) return;
    
    // Don't process if clicking on the thumb
    if (e.target.classList.contains('scrollbar-thumb')) return;
    
    const scrollbarRect = scrollbarRef.current.getBoundingClientRect();
    const clickPosition = (e.clientX - scrollbarRect.left) / scrollbarRect.width;
    
    const container = containerRef.current;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const newScrollPosition = clickPosition * maxScroll;
    
    container.scrollLeft = newScrollPosition;
    scrollPositionRef.current = newScrollPosition;
    setScrollbarPosition(clickPosition);
  };

  // Handle thumb drag
  const handleThumbMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!containerRef.current || !scrollbarRef.current) return;
    
    isDraggingRef.current = true;
    
    const scrollbarRect = scrollbarRef.current.getBoundingClientRect();
    const thumbWidth = 24; // Width of the thumb in pixels (reduced from 32)
    const effectiveWidth = scrollbarRect.width - thumbWidth;
    const startX = e.clientX;
    const startScrollbarPosition = scrollbarPosition;
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaRatio = deltaX / effectiveWidth;
      let newPosition = startScrollbarPosition + deltaRatio;
      
      // Clamp position between 0 and 1
      newPosition = Math.max(0, Math.min(1, newPosition));
      
      const container = containerRef.current;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const newScrollPosition = newPosition * maxScroll;
      
      container.scrollLeft = newScrollPosition;
      scrollPositionRef.current = newScrollPosition;
      setScrollbarPosition(newPosition);
    };
    
    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Order button click handlers with immediate animation restart
  const handleOrderChange = (order) => {
    setWeaponsOrderBy(order);
  };

  return (
    <div className="mb-16 mt-8" ref={componentRef}>
      <SectionHeader title="Legendary Weapons" />
      
      {/* Pretext */}
      {weapons && weapons.pretext && (
        <p className="mb-4 text-[#333333] dark:text-white">{weapons.pretext}</p>
      )}
      
      {/* Scrolling Weapons Display */}
      <div 
        className="relative"
        onMouseEnter={() => setIsHoveringContainer(true)}
        onMouseLeave={() => setIsHoveringContainer(false)}
      >
        {/* Order buttons and Key button container */}
        <div className={`flex justify-between mb-4 transition-opacity duration-300 ${isHoveringContainer ? 'opacity-100' : 'opacity-0'}`}>
          {/* Key button */}
          {weapons && weapons.key && (
            <div 
              className="relative inline-flex items-center bg-gray-200 dark:bg-gray-700 rounded-full p-1 cursor-pointer shadow-md"
              onMouseEnter={handleKeyMouseEnter}
              onMouseLeave={handleKeyMouseLeave}
              onMouseMove={handleKeyMouseMove}
            >
              <button 
                className="relative z-10 px-5 py-2 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[80px]"
              >
                Key
              </button>
            </div>
          )}
          
          {/* Order buttons */}
          <div className="relative inline-flex items-center bg-gray-200 dark:bg-gray-700 rounded-full p-1 cursor-pointer shadow-md">
            <div 
              className={`absolute h-full top-0 rounded-full transition-all duration-300 ${
                weaponsOrderBy === 'contribution' ? 'left-0 w-1/2 bg-[#2978C8] dark:bg-[#4f9cf9]' : 'left-1/2 w-1/2 bg-[#294C6F] dark:bg-[#3a6ca0]'
              }`}
            ></div>
            <button 
              onClick={() => handleOrderChange('contribution')}
              className={`relative z-10 px-5 py-2 rounded-full text-sm font-medium transition-colors duration-300 min-w-[140px] ${
                weaponsOrderBy === 'contribution' ? 'text-white' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              Contribution Order
            </button>
            <button 
              onClick={() => handleOrderChange('release')}
              className={`relative z-10 px-5 py-2 rounded-full text-sm font-medium transition-colors duration-300 min-w-[140px] ${
                weaponsOrderBy === 'release' ? 'text-white' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              Release Order
            </button>
          </div>
        </div>

        <div ref={containerRef} className="overflow-x-hidden py-8">
          <div className="flex">
            {orderedWeapons.map((weapon) => (
              <div 
                key={weapon.name}
                className="flex flex-col items-center min-w-[200px] px-4"
                onMouseEnter={(e) => handleMouseEnter(weapon, e)}
                onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={handleMouseLeave}
              >
                <img 
                  src={hoveredWeapon === weapon && weapon.gif ? `${process.env.PUBLIC_URL}${weapon.gif}` : `${process.env.PUBLIC_URL}${weapon.image}`}
                  alt={weapon.name}
                  className="h-32 w-32 object-contain"
                />
                <p className="mt-2 text-center text-[#294C6F] dark:text-[#4f9cf9]">{weapon.name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Improved Scrollbar with smaller thumb - only visible when hovering */}
        <div 
          ref={scrollbarRef}
          className={`h-8 bg-transparent rounded-full relative mx-4 -mt-2 cursor-pointer transition-opacity duration-300 ${isHoveringContainer ? 'opacity-100' : 'opacity-0'}`}
          onClick={handleScrollbarClick}
        >
          <div className="h-2 bg-[#23014b]/20 dark:bg-[#4f9cf9]/20 rounded-full absolute top-1/2 transform -translate-y-1/2 w-full"></div>
          <div 
            className="scrollbar-thumb absolute top-1/2 transform -translate-y-1/2 h-6 w-6 bg-[#4a9eff] dark:bg-[#4f9cf9] rounded-full shadow-md cursor-grab active:cursor-grabbing"
            style={{
              left: `calc(${scrollbarPosition * 100}% - 12px)`
            }}
            onMouseDown={handleThumbMouseDown}
          ></div>
        </div>
      </div>
      
      {/* Description */}
      {weapons && weapons.description && (
        <div className="text-lg text-[#333333] dark:text-white leading-relaxed mb-4 mt-4">
          {renderParagraphs(weapons.description)}
        </div>
      )}
      
      {/* More Details Toggleable Section */}
      {weapons && weapons.moreDetails && (
        <ToggleableDetails title="More Details" paragraphs={weapons.moreDetails} />
      )}
      
      {/* Post Details */}
      {weapons && weapons.postDetails && (
        <div className="text-lg text-[#333333] dark:text-white leading-relaxed mb-4">
          {renderParagraphs(weapons.postDetails)}
        </div>
      )}

      {/* YouTube Link for Legendary Weapons */}
      <div className="mb-6 text-center">
        <a 
          href="https://www.youtube.com/watch?v=k2eUWFo5f2o" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center text-[#2978C8] hover:text-[#294C6F] dark:text-[#4f9cf9] dark:hover:text-[#7fb5fa] transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.615 3.184c-1.25-.5-2.5-.5-3.75-.5H8.135c-1.25 0-2.5 0-3.75.5C1.5 3.684 0 5.184 0 7.5v9c0 2.316 1.5 3.816 4.385 4.316 1.25.5 2.5.5 3.75.5h7.73c1.25 0 2.5 0 3.75-.5C22.5 20.316 24 18.816 24 16.5v-9c0-2.316-1.5-3.816-4.385-4.316zM9.545 15.537V8.463l6.001 3.037-6.001 3.037z"/>
          </svg>
          Watch Hoplite Particle Effect & Sound Design showcase on YouTube
        </a>
      </div>

      {/* Weapon tooltip */}
      {hoveredWeapon && (
        <div 
          className="fixed z-50 pointer-events-none"
          style={{ 
            left: `${mousePos.x + 10}px`, 
            top: `${mousePos.y - 10}px`,
            transform: 'translate(0, -100%)'
          }}
        >
          <div className="bg-[#23014b] dark:bg-[#1a1a2e] rounded-lg p-4 shadow-lg max-w-[600px]">
            <p className="text-[#FFD700] text-lg font-minecraft mb-1 break-words shadow-sm">{hoveredWeapon.name}</p>
            {hoveredWeapon.functionality && (
              <p className="text-gray-300 font-minecraft text-sm mb-2 break-words shadow-sm">{hoveredWeapon.functionality}</p>
            )}
            <p className="text-white font-minecraft text-base break-words shadow-sm">{hoveredWeapon.description}</p>
            {showTrivia && hoveredWeapon.trivia && (
              <p className="text-white font-minecraft text-sm break-words mt-2 shadow-sm">
                <span className="text-white">Trivia: </span>{hoveredWeapon.trivia}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Key tooltip */}
      {isHoveringKey && weapons && weapons.key && (
        <div 
          className="fixed z-50 pointer-events-none"
          style={{ 
            left: `${mousePos.x + 10}px`, 
            top: `${mousePos.y - 10}px`,
            transform: 'translate(0, -100%)'
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 pt-3 shadow-lg max-w-[800px] border border-gray-200 dark:border-gray-700">
            <div className="text-[#333333] dark:text-white text-base">
              {weapons.key.map((item, index) => {
                // Only show the last item if trivia mode is enabled
                if (index === weapons.key.length - 1 && !showTrivia) {
                  return null;
                }
                
                // Split the text at the colon to bold the first part
                const parts = item.split(':');
                if (parts.length > 1) {
                  return (
                    <p key={index} className="mb-1 leading-tight">
                      <span className="font-bold">{parts[0]}:</span>
                      {parts[1]}
                    </p>
                  );
                } else {
                  return <p key={index} className="mb-1 leading-tight">{item}</p>;
                }
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegendaryWeapons;